import routes from '../../../router/routes.dictionary'
import localStorage from '../LocalStorage/localStorage'
import { UserRole } from './userRoleManagement.types'

const USER_ROLE_KEY = 'userRole'

/**
 * Maps frontend UserRole enum values to backend role format
 * @param role - The frontend user role
 * @returns The backend role format
 */
const mapToBackendRole = (role: UserRole | null): string => {
  if (!role) return ''

  // Map frontend camelCase to backend snake_case
  const roleMap: Record<UserRole, string> = {
    [UserRole.admin]: 'admin',
    [UserRole.traveler]: 'traveler',
    [UserRole.shopper]: 'shopper',
    [UserRole.sender]: 'sender',
    [UserRole.logisticsPartner]: 'logistics_partner'
  }

  return roleMap[role] || role
}

/**
 * Sets the user role in local storage
 * @param role - The user role to set
 */
export const setUserRole = (role: UserRole): void => {
  localStorage.setItem(USER_ROLE_KEY, role)
}

/**
 * Gets the user role from local storage
 * @returns The user role or null if not set
 */
export const getUserRole = (): UserRole | null => {
  const role = localStorage.getItem(USER_ROLE_KEY)

  return role ? (role as UserRole) : null
}

/**
 * Gets the user role in backend format (snake_case)
 * @returns The backend role format or empty string if not set
 */
export const getUserRoleForBackend = (): string => {
  const role = getUserRole()

  return mapToBackendRole(role)
}

/**
 * Removes the user role from local storage
 */
export const removeUserRole = (): void => {
  localStorage.removeItem(USER_ROLE_KEY)
}

type RouteConfig = (typeof routes)[keyof typeof routes]

/**
 * Gets the user role associated with a specific pathname by looking up
 * the route in the routes dictionary. Returns the first role if multiple
 * roles are associated with the route.
 * @param pathname - The pathname to look up (e.g., '/fulfillment-orders', '/shop')
 * @returns The UserRole for the route, or null if not found or no role assigned
 */
export const getRoleFromPath = (pathname: string): UserRole | null => {
  // Handle root path specially
  if (pathname === '/' || pathname === '') {
    const homeRoute = routes.home
    if ('userRole' in homeRoute && homeRoute.userRole && homeRoute.userRole.length > 0) {
      return homeRoute.userRole[0]
    }

    return null
  }

  // Remove leading slash for comparison
  const normalizedPath = pathname.replace(/^\//, '')

  // Handle empty string after normalization (was just '/')
  if (normalizedPath === '') {
    const homeRoute = routes.home
    if ('userRole' in homeRoute && homeRoute.userRole && homeRoute.userRole.length > 0) {
      return homeRoute.userRole[0]
    }

    return null
  }

  const routeEntries = Object.entries(routes) as [string, RouteConfig][]

  const matchingRoute = routeEntries.find(([, routeConfig]) => {
    const routePath = routeConfig.path

    // Skip root path in iteration (handled above)
    if (routePath === '/') {
      return false
    }

    return normalizedPath === routePath || normalizedPath.startsWith(`${routePath}/`)
  })

  if (!matchingRoute) {
    return null
  }

  const [, routeConfig] = matchingRoute

  if ('userRole' in routeConfig && routeConfig.userRole && routeConfig.userRole.length > 0) {
    return routeConfig.userRole[0]
  }

  return null
}
